import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, DateTimeField, Dialog, TextField } from "@core/components";
import { useIdempotencyKey } from "@core/hooks";
import { classifyAppelFailure } from "../../_shared/failure";
import type { Appel } from "../../_shared/types";
import { useCreateAppel } from "../hooks";
import {
  initialValues,
  toCreateRequest,
  validateCreate,
  type CreateAppelValues,
} from "../types";

interface CreateAppelDialogProps {
  cohortId: string;
  /** The name the server would give (« Appel 2 »): the dialog starts with it. */
  suggestedName: string;
  onClose: () => void;
  /** Called with the new appel once the server saved it; the caller opens its first step. */
  onCreated: (appel: Appel) => void;
}

const FORM_ID = "create-appel-form";

/**
 * « Créer un appel » (Figma "Create appel"): the name (filled in with the next number), the opening
 * and the closing, each a date and a time in Algeria time. « Créer l'appel » saves the appel as a
 * draft at once, then the caller opens step 1: nothing waits for the other fields.
 *
 * Mounted only while open, so every opening starts clean. The same idempotency key is kept while
 * the values stay the same, so a retry after an uncertain answer never creates two appels; it
 * changes with the values and after a refusal. A name already used in the cohort is refused on the
 * name; a closing that does not follow the opening, on the closing.
 */
export function CreateAppelDialog({
  cohortId,
  suggestedName,
  onClose,
  onCreated,
}: CreateAppelDialogProps) {
  const { t } = useTranslation("appels");
  const create = useCreateAppel(cohortId);
  const [values, setValues] = useState<CreateAppelValues>(() =>
    initialValues(suggestedName)
  );
  const [submitted, setSubmitted] = useState(false);
  const { key, rotate } = useIdempotencyKey(JSON.stringify(values));

  const local = submitted ? validateCreate(values) : {};
  const failure = create.error ? classifyAppelFailure(create.error) : null;
  // A refusal belongs to the values it was made for: editing anything clears it.
  const applies =
    failure !== null &&
    create.variables !== undefined &&
    JSON.stringify(create.variables.body) ===
      JSON.stringify(toCreateRequest(values));

  const message = (code: string | undefined): string | undefined =>
    code ? t(`errors.${code}` as "errors.date_required") : undefined;

  const nameError =
    applies && failure.kind === "name_taken"
      ? t("errors.name_taken")
      : undefined;
  const closingError =
    message(local.closing) ??
    (applies && failure.kind === "validation" && failure.fields.closing_at
      ? t("errors.closing_before_opening")
      : undefined);
  const generalError = !applies
    ? null
    : failure.kind === "cohort_archived"
      ? t("errors.cohort_archived")
      : failure.kind === "forbidden"
        ? t("errors.forbidden")
        : failure.kind === "network"
          ? t("errors.network")
          : failure.kind === "rate_limited"
            ? t("errors.rate_limited")
            : failure.kind === "unknown"
              ? t("errors.unknown")
              : null;

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(validateCreate(values)).length > 0) return;
    create.mutate(
      { body: toCreateRequest(values), idempotencyKey: key },
      {
        onSuccess: onCreated,
        onError: () => {
          rotate();
        },
      }
    );
  };

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("create.title")}
      hasCloseButton
      className="max-w-130"
      actions={
        <>
          <Button
            variant="secondary"
            onPress={onClose}
            isDisabled={create.isPending}
          >
            {t("create.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            isPending={create.isPending}
            isDisabled={create.isPending}
          >
            {t("create.submit")}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        noValidate
        className="flex flex-col gap-md"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          submit();
        }}
      >
        <TextField
          label={t("create.name")}
          autoComplete="off"
          value={values.name}
          onChange={(name) => {
            setValues({ ...values, name });
          }}
          isDisabled={create.isPending}
          errorMessage={nameError}
        />
        <DateTimeField
          label={t("create.opening")}
          timeLabel={t("create.time")}
          value={values.opening}
          onChange={(opening) => {
            setValues({ ...values, opening });
          }}
          isDisabled={create.isPending}
          errorMessage={message(local.opening)}
          timeErrorMessage={message(local.openingTime)}
        />
        <DateTimeField
          label={t("create.closing")}
          timeLabel={t("create.time")}
          value={values.closing}
          onChange={(closing) => {
            setValues({ ...values, closing });
          }}
          isDisabled={create.isPending}
          errorMessage={closingError}
          timeErrorMessage={message(local.closingTime)}
        />
        {generalError ? (
          <p role="alert" className="text-sm-medium text-(--text-error)">
            {generalError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
